import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  FileText,
  Filter,
  Loader2,
  Mail,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import CampaignRichTextEditor from './CampaignRichTextEditor';
import { supabase } from '../../lib/supabaseClient';
import { renderEmailTemplateToHtml } from '../../lib/emailTemplateRenderer';

export type StepCondition = 'everyone' | 'not_opened' | 'opened_no_click' | 'clicked';

export interface ExtendedStepVariant {
  name: string; // 'b', 'c', 'd' — 'a' is the step's own subject/content
  subject: string;
  html_content: string;
}

export interface ExtendedStepDraft {
  id: string;
  delayValue: number;
  delayUnit: 'hours' | 'days';
  condition: StepCondition;
  subject: string;
  html_content: string;
  variants: ExtendedStepVariant[];
  /** UI-only: how the content was authored. */
  contentMode?: 'custom' | 'template';
  templateId?: string;
}

interface EmailTemplateRow {
  id: string;
  name: string;
  subject: string | null;
  content: unknown;
  template_type?: string | null;
}

/** Extract sendable HTML from an email_templates row (same fallbacks as the main builder). */
function templateToHtml(
  template: EmailTemplateRow & { body?: string; html_content?: string },
  businessName: string,
  logoUrl?: string
): string {
  const content = template.content as any;
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object') {
    if (content.html) return content.html;
    if (content.body) return content.body;
    if (Array.isArray(content.sections)) {
      return renderEmailTemplateToHtml({
        sections: content.sections,
        businessName: businessName || 'Your Business',
        logoUrl,
      });
    }
  }
  if (template.body) return template.body;
  if (template.html_content) return template.html_content;
  return '';
}

export const STEP_CONDITION_LABELS: Record<StepCondition, string> = {
  everyone: 'Everyone',
  not_opened: "Didn't open",
  opened_no_click: 'Opened, no click',
  clicked: 'Clicked',
};

const CONDITION_OPTIONS: Array<{ value: StepCondition; label: string; hint: string }> = [
  { value: 'everyone', label: 'Everyone', hint: 'All recipients of the previous email' },
  { value: 'not_opened', label: "Didn't open", hint: 'Did not open the previous email' },
  { value: 'opened_no_click', label: 'Opened, no click', hint: 'Opened but clicked nothing' },
  { value: 'clicked', label: 'Clicked', hint: 'Clicked a link in the previous email' },
];

const DELAY_PRESETS: Array<{ label: string; value: number; unit: 'hours' | 'days' }> = [
  { label: '1 day', value: 1, unit: 'days' },
  { label: '2 days', value: 2, unit: 'days' },
  { label: '3 days', value: 3, unit: 'days' },
  { label: '1 week', value: 7, unit: 'days' },
];

const MAX_FOLLOW_UPS = 4; // 5 emails total including the initial one

function newStep(): ExtendedStepDraft {
  return {
    id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    delayValue: 2,
    delayUnit: 'days',
    condition: 'not_opened',
    subject: '',
    html_content: '',
    variants: [],
    contentMode: 'custom',
    templateId: '',
  };
}

export function stepDelayLabel(step: ExtendedStepDraft): string {
  return `${step.delayValue} ${step.delayUnit === 'hours' ? (step.delayValue === 1 ? 'hour' : 'hours') : step.delayValue === 1 ? 'day' : 'days'}`;
}

interface ExtendedCampaignEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (steps: ExtendedStepDraft[]) => void;
  initialSteps: ExtendedStepDraft[];
  businessId?: string;
  businessName?: string;
  logoUrl?: string;
  /** Summary of the initial email configured in the main builder. */
  initialEmailSummary: { subject: string; variantCount: number };
}

/**
 * Full-screen timeline editor for extended (multi-step) email campaigns.
 * The initial email is authored in the main builder; this editor manages the
 * follow-up emails (wait + condition + content, optional A/B per step).
 */
export default function ExtendedCampaignEditor({
  isOpen,
  onClose,
  onSave,
  initialSteps,
  businessId,
  businessName,
  logoUrl,
  initialEmailSummary,
}: ExtendedCampaignEditorProps) {
  const [steps, setSteps] = useState<ExtendedStepDraft[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateRow[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const seeded = initialSteps.length > 0 ? initialSteps : [newStep()];
      setSteps(seeded);
      setExpandedId(seeded[0]?.id ?? null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load the business's email templates for the per-step template picker.
  useEffect(() => {
    if (!isOpen || !businessId || templates.length > 0) return;
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const { data, error: templatesError } = await supabase
          .from('email_templates')
          .select('id, name, subject, content, template_type, one_off')
          .eq('business_id', businessId)
          .eq('one_off', false)
          .order('name');
        if (!cancelled && !templatesError) {
          setTemplates((data as EmailTemplateRow[]) || []);
        }
      } catch (err) {
        console.error('Error loading email templates:', err);
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessId]);

  const applyTemplateToStep = (stepId: string, templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    const html = templateToHtml(template as any, businessName || '', logoUrl);
    setSteps((prev) =>
      prev.map((s) =>
        s.id === stepId
          ? {
              ...s,
              templateId,
              contentMode: 'template',
              subject: template.subject || template.name || s.subject,
              html_content: html || '<p>No content available</p>',
            }
          : s
      )
    );
  };

  const updateStep = (id: string, patch: Partial<ExtendedStepDraft>) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const removeStep = (id: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== id));
  };

  const addStep = () => {
    const step = newStep();
    setSteps((prev) => [...prev, step]);
    setExpandedId(step.id);
  };

  const addVariant = (stepId: string) => {
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== stepId) return s;
        const nextName = String.fromCharCode(98 + s.variants.length); // b, c, d…
        return {
          ...s,
          variants: [...s.variants, { name: nextName, subject: '', html_content: '' }],
        };
      })
    );
  };

  const updateVariant = (stepId: string, name: string, patch: Partial<ExtendedStepVariant>) => {
    setSteps((prev) =>
      prev.map((s) =>
        s.id === stepId
          ? { ...s, variants: s.variants.map((v) => (v.name === name ? { ...v, ...patch } : v)) }
          : s
      )
    );
  };

  const removeVariant = (stepId: string, name: string) => {
    setSteps((prev) =>
      prev.map((s) => {
        if (s.id !== stepId) return s;
        const remaining = s.variants.filter((v) => v.name !== name);
        // Re-letter so names stay contiguous (b, c, d…)
        const relettered = remaining.map((v, i) => ({ ...v, name: String.fromCharCode(98 + i) }));
        return { ...s, variants: relettered };
      })
    );
  };

  const validation = useMemo(() => {
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i];
      if (s.contentMode === 'template' && !s.templateId)
        return `Email ${i + 2}: choose a template or switch to Custom.`;
      if (!s.subject.trim()) return `Email ${i + 2} needs a subject.`;
      if (!s.html_content.trim()) return `Email ${i + 2} needs content.`;
      if (!s.delayValue || s.delayValue < 1) return `Email ${i + 2} needs a wait time of at least 1 ${s.delayUnit === 'hours' ? 'hour' : 'day'}.`;
      if (s.delayUnit === 'days' && s.delayValue > 30) return `Email ${i + 2}: wait time can be at most 30 days.`;
      if (s.delayUnit === 'hours' && s.delayValue > 720) return `Email ${i + 2}: wait time can be at most 720 hours.`;
      for (const v of s.variants) {
        if (!v.subject.trim() || !v.html_content.trim())
          return `Email ${i + 2}, variant ${v.name.toUpperCase()} needs a subject and content.`;
      }
    }
    return null;
  }, [steps]);

  const handleSave = () => {
    if (steps.length === 0) {
      onSave([]);
      onClose();
      return;
    }
    if (validation) {
      setError(validation);
      return;
    }
    onSave(steps);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100200] flex flex-col bg-gray-50"
      role="dialog"
      aria-modal="true"
      // Full-screen editor — keep header/footer clear of the iOS notch & home indicator
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">Extended campaign</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Add follow-up emails that send automatically based on how people engage.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto ios-momentum-scroll px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col">
          {/* Initial email (authored in the main builder) */}
          <div className="rounded-2xl border-2 border-blue-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-600">
                <Mail className="h-4.5 w-4.5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">
                  Email 1 · Initial email
                </p>
                <p className="truncate text-sm font-medium text-gray-900">
                  {initialEmailSummary.subject.trim() || 'Untitled — set the subject in the main builder'}
                </p>
              </div>
              {initialEmailSummary.variantCount > 1 ? (
                <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                  {initialEmailSummary.variantCount}-way split
                </span>
              ) : null}
            </div>
            <p className="mt-2 text-[12px] text-gray-500">
              The initial email is edited in the main builder. Follow-ups below build on it.
            </p>
          </div>

          {/* Follow-up steps */}
          {steps.map((step, index) => {
            const emailNumber = index + 2;
            const expanded = expandedId === step.id;
            return (
              <div key={step.id}>
                {/* Connector */}
                <div className="mx-auto flex w-fit flex-col items-center py-1">
                  <div className="h-5 w-px bg-gray-300" />
                  <div className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600">
                    <Clock className="h-3 w-3 text-gray-400" />
                    wait {stepDelayLabel(step)}
                    <span className="text-gray-300">·</span>
                    <Filter className="h-3 w-3 text-gray-400" />
                    {STEP_CONDITION_LABELS[step.condition]}
                  </div>
                  <div className="h-5 w-px bg-gray-300" />
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white">
                  {/* Card header */}
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : step.id)}
                    className="flex w-full items-center gap-3 p-4 text-left"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                      <Mail className="h-4.5 w-4.5 text-gray-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        Email {emailNumber}
                      </p>
                      <p className="truncate text-sm font-medium text-gray-900">
                        {step.subject.trim() || 'Untitled follow-up'}
                      </p>
                    </div>
                    {step.variants.length > 0 ? (
                      <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                        {step.variants.length + 1}-way split
                      </span>
                    ) : null}
                    {expanded ? (
                      <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                    )}
                  </button>

                  {expanded ? (
                    <div className="space-y-5 border-t border-gray-100 p-4">
                      {/* Wait */}
                      <div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                          Wait after previous email
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            max={step.delayUnit === 'hours' ? 720 : 30}
                            value={step.delayValue}
                            onChange={(e) =>
                              updateStep(step.id, { delayValue: Math.max(1, parseInt(e.target.value, 10) || 1) })
                            }
                            className="w-20 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          />
                          <select
                            value={step.delayUnit}
                            onChange={(e) =>
                              updateStep(step.id, { delayUnit: e.target.value as 'hours' | 'days' })
                            }
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="hours">hours</option>
                            <option value="days">days</option>
                          </select>
                          <div className="flex flex-wrap gap-1.5">
                            {DELAY_PRESETS.map((preset) => (
                              <button
                                key={preset.label}
                                type="button"
                                onClick={() =>
                                  updateStep(step.id, { delayValue: preset.value, delayUnit: preset.unit })
                                }
                                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                  step.delayValue === preset.value && step.delayUnit === preset.unit
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {preset.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Condition */}
                      <div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">
                          Send to (based on the previous email)
                        </label>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {CONDITION_OPTIONS.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => updateStep(step.id, { condition: opt.value })}
                              className={`rounded-xl border-2 px-2.5 py-2 text-left transition-colors ${
                                step.condition === opt.value
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:border-blue-200'
                              }`}
                            >
                              <span className="block text-[12px] font-semibold text-gray-900">{opt.label}</span>
                              <span className="mt-0.5 block text-[10px] leading-tight text-gray-500">{opt.hint}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Content mode: custom vs template */}
                      <div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">Email content</label>
                        <div className="mb-3 inline-flex rounded-lg bg-gray-100 p-0.5">
                          <button
                            type="button"
                            onClick={() => updateStep(step.id, { contentMode: 'custom' })}
                            className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
                              (step.contentMode || 'custom') === 'custom'
                                ? 'bg-gray-900 text-white'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Custom
                          </button>
                          <button
                            type="button"
                            onClick={() => updateStep(step.id, { contentMode: 'template' })}
                            className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
                              step.contentMode === 'template'
                                ? 'bg-gray-900 text-white'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Template
                          </button>
                        </div>

                        {step.contentMode === 'template' ? (
                          <div className="mb-3">
                            {loadingTemplates ? (
                              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-[12px] text-gray-500">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading templates…
                              </div>
                            ) : templates.length === 0 ? (
                              <p className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-[12px] text-gray-500">
                                No templates yet — create one in the Email Templates section, or switch to Custom.
                              </p>
                            ) : (
                              <div className="grid max-h-48 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
                                {templates.map((template) => (
                                  <button
                                    key={template.id}
                                    type="button"
                                    onClick={() => applyTemplateToStep(step.id, template.id)}
                                    className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                                      step.templateId === template.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:border-blue-200'
                                    }`}
                                  >
                                    <FileText
                                      className={`h-4 w-4 shrink-0 ${
                                        step.templateId === template.id ? 'text-blue-600' : 'text-gray-400'
                                      }`}
                                    />
                                    <span className="min-w-0">
                                      <span className="block truncate text-[13px] font-medium text-gray-900">
                                        {template.name}
                                      </span>
                                      {template.subject ? (
                                        <span className="block truncate text-[11px] text-gray-500">
                                          {template.subject}
                                        </span>
                                      ) : null}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>

                      <div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">Subject</label>
                        <input
                          type="text"
                          value={step.subject}
                          onChange={(e) => updateStep(step.id, { subject: e.target.value })}
                          placeholder="Follow-up subject line…"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {step.contentMode === 'template' && step.templateId ? (
                        <p className="-mt-3 text-[11px] text-gray-400">
                          Using template "{templates.find((t) => t.id === step.templateId)?.name}". You can still tweak
                          the subject above, or switch to Custom to edit the body.
                        </p>
                      ) : null}
                      {(step.contentMode || 'custom') === 'custom' ? (
                        <div>
                          <label className="mb-1.5 block text-[12px] font-semibold text-gray-700">Email body</label>
                          <CampaignRichTextEditor
                            value={step.html_content}
                            onChange={(html) => updateStep(step.id, { html_content: html })}
                            placeholder="Write the follow-up email…"
                            rows={5}
                            businessId={businessId}
                          />
                        </div>
                      ) : null}

                      {/* Variants */}
                      <div>
                        <div className="mb-1.5 flex items-center justify-between">
                          <label className="text-[12px] font-semibold text-gray-700">
                            Split test this email {step.variants.length > 0 ? `(${step.variants.length + 1} versions)` : ''}
                          </label>
                          {step.variants.length < 3 ? (
                            <button
                              type="button"
                              onClick={() => addVariant(step.id)}
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-blue-600 transition-colors hover:bg-blue-50"
                            >
                              <Plus className="h-3.5 w-3.5" /> Add variant
                            </button>
                          ) : null}
                        </div>
                        {step.variants.length === 0 ? (
                          <p className="text-[11px] text-gray-400">
                            Optional — add versions and recipients are split evenly between them.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {step.variants.map((variant) => (
                              <div key={variant.name} className="rounded-xl border border-violet-200 bg-violet-50/40 p-3">
                                <div className="mb-2 flex items-center justify-between">
                                  <span className="text-[11px] font-bold uppercase text-violet-700">
                                    Variant {variant.name.toUpperCase()}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeVariant(step.id, variant.name)}
                                    className="rounded-md p-1 text-gray-400 transition-colors hover:bg-white hover:text-red-500"
                                    aria-label={`Remove variant ${variant.name.toUpperCase()}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={variant.subject}
                                  onChange={(e) => updateVariant(step.id, variant.name, { subject: e.target.value })}
                                  placeholder="Variant subject…"
                                  className="mb-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                                />
                                <CampaignRichTextEditor
                                  value={variant.html_content}
                                  onChange={(html) => updateVariant(step.id, variant.name, { html_content: html })}
                                  placeholder="Variant email body…"
                                  rows={4}
                                  businessId={businessId}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Delete step */}
                      <div className="flex justify-end border-t border-gray-100 pt-3">
                        <button
                          type="button"
                          onClick={() => removeStep(step.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12px] font-medium text-red-600 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Remove this email
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}

          {/* Add follow-up */}
          {steps.length < MAX_FOLLOW_UPS ? (
            <div className="mx-auto flex w-fit flex-col items-center pt-1">
              <div className="h-5 w-px bg-gray-300" />
              <button
                type="button"
                onClick={addStep}
                className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-gray-300 bg-white px-4 py-2 text-[13px] font-medium text-gray-600 transition-colors hover:border-blue-400 hover:text-blue-600"
              >
                <Plus className="h-4 w-4" /> Add follow-up email
              </button>
            </div>
          ) : (
            <p className="pt-3 text-center text-[11px] text-gray-400">
              Maximum of {MAX_FOLLOW_UPS + 1} emails per campaign.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3">
          <div className="min-w-0">
            {error ? (
              <p className="text-[12px] font-medium text-red-600">{error}</p>
            ) : (
              <p className="text-[12px] text-gray-500">
                {steps.length === 0
                  ? 'No follow-ups — saving turns this back into a normal campaign.'
                  : `${steps.length + 1} emails total. Follow-ups are billed when they send, based on who matches.`}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Save extended campaign
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
